import { useTranslation } from 'next-i18next';
import Head from 'next/head';
import { useRouter } from 'next/router';
import type { ReactNode } from 'react';

import { type Locale, useLang } from '@inflearn/i18n-tools';
import { NEXT_PUBLIC_CDN_URL, NEXT_PUBLIC_ENV } from '../../../appConfig';
import { generateSiteURL } from '../../utils/url';

export type CustomHeadProps = {
  title?: string;
  siteTitle?: string;
  description?: string;
  pageCategory?: string;
  image?: string;
  robots?: boolean;
  children?: ReactNode;
};

const MAIN_IMG_BANNER = `${NEXT_PUBLIC_CDN_URL}/assets/brand/inflearn-brand.png`;
const DEFAULT_TITLE = '메타.기본.제목';
const DEFAULT_SUB_TITLE = '인프런';
const DEFAULT_DESCRIPTION = '메타.기본.설명';
const MAX_TITLE_LENGTH = 55;
const MAX_DESCRIPTION_LENGTH = 152;

const sanitizeText = (text: string) => text.replace(/"/g, "'");
const truncateText = (text: string, length: number) =>
  text.length <= length ? text : text.slice(0, length) + '...';
const localeToOgLocale = (locale: Locale) => locale.replace('-', '_');

export default function CustomHead({
  title,
  siteTitle,
  description,
  pageCategory,
  robots = true,
  image = MAIN_IMG_BANNER,
  children,
}: CustomHeadProps) {
  const { t } = useTranslation();
  const { locale } = useLang();

  const router = useRouter();
  const fullUrl = generateSiteURL(router.asPath);

  const _robots = NEXT_PUBLIC_ENV === 'production' ? robots : false;

  const subTitle = siteTitle
    ? ` - ${siteTitle}${pageCategory ? ` | ${pageCategory}` : ''}`
    : ` - ${t(DEFAULT_SUB_TITLE)}`;

  const _title = title
    ? `${truncateText(title, MAX_TITLE_LENGTH - subTitle.length)}${subTitle}`
    : t(DEFAULT_TITLE);
  const _description = description
    ? truncateText(description, MAX_DESCRIPTION_LENGTH)
    : t(DEFAULT_DESCRIPTION);

  return (
    <Head>
      <title>{_title}</title>
      <meta name="description" content={_description} />
      <meta name="viewport" content="width=device-width, initial-scale=1" />
      <meta
        name="robots"
        content={`${_robots ? 'index, follow, max-image-preview:large' : 'noindex, nofollow'}`}
      />
      <meta charSet="utf-8" />
      {/* Canonical URL */}
      {robots && <link rel="canonical" href={fullUrl} />}

      {/* Open Graph Tags */}
      <meta property="og:url" content={fullUrl} />
      <meta property="og:type" content="website" />
      <meta property="og:title" content={sanitizeText(_title)} />
      <meta property="og:description" content={sanitizeText(_description)} />
      <meta property="og:image" content={image} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:secure_url" content={image} />
      <meta property="og:site_name" content={t('인프런')} />
      <meta property="og:locale" content={localeToOgLocale(locale)} />

      {/* Twitter Tags */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={sanitizeText(_title)} />
      <meta name="twitter:description" content={sanitizeText(_description)} />
      <meta name="twitter:image" content={image} />
      <meta name="twitter:url" content={fullUrl} />
      <meta name="twitter:site" content="@inflearn" />
      <meta name="twitter:creator" content="@inflearn" />

      {/* Google+ Tags */}
      <meta itemProp="name" content={sanitizeText(_title)} />
      <meta itemProp="description" content={sanitizeText(_description)} />
      <meta itemProp="image" content={image} />

      {/* Favicon and Icons */}
      <link rel="icon" href={`${NEXT_PUBLIC_CDN_URL}/dist/favicon.ico`} />
      <link
        rel="icon"
        type="image/png"
        sizes="512x512"
        href={`${NEXT_PUBLIC_CDN_URL}/dist/icon-512x512.png`}
      />
      <link
        rel="apple-touch-icon"
        sizes="512x512"
        href={`${NEXT_PUBLIC_CDN_URL}/dist/icon-512x512.png`}
      />
      <link
        rel="icon"
        type="image/png"
        sizes="192x192"
        href={`${NEXT_PUBLIC_CDN_URL}/dist/icon-192x192.png`}
      />
      <link
        rel="apple-touch-icon"
        sizes="192x192"
        href={`${NEXT_PUBLIC_CDN_URL}/dist/icon-192x192.png`}
      />
      <link
        rel="icon"
        type="image/png"
        sizes="128x128"
        href={`${NEXT_PUBLIC_CDN_URL}/dist/icon-128x128.png`}
      />
      <link
        rel="apple-touch-icon"
        sizes="128x128"
        href={`${NEXT_PUBLIC_CDN_URL}/dist/icon-128x128.png`}
      />
      <link rel="shortcut icon" href={`${NEXT_PUBLIC_CDN_URL}/dist/favicon.ico`} />
      {children}
    </Head>
  );
}
